import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });
export { client };

// On 401, try one silent refresh then retry the original request
client.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config || {};
    if (error.response?.status === 401 && !orig._retry && !String(orig.url).includes("/auth/")) {
      orig._retry = true;
      try {
        await client.post("/auth/refresh");
        return client(orig);
      } catch {
        window.dispatchEvent(new Event("mai-auth-expired"));
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  demoLogin: (persona) => client.post("/auth/demo-login", { persona }).then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
};

export const api = {
  agenda: () => client.get("/agenda").then((r) => r.data),
  kols: () => client.get("/kols").then((r) => r.data),
  kol: (id) => client.get(`/kols/${id}`).then((r) => r.data),
  getBrief: (id) => client.get(`/kols/${id}/brief`).then((r) => r.data),
  genBrief: (id) => client.post(`/kols/${id}/brief`).then((r) => r.data),
  transcript: (payload) => client.post("/interactions/transcript", payload).then((r) => r.data),
  voice: (formData) => client.post("/interactions/voice", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  documentCapture: (formData) => client.post("/interactions/document", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  attachPhoto: (id, formData) => client.post(`/interactions/${id}/photo`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  removePhoto: (id, attId) => client.delete(`/interactions/${id}/photo/${attId}`).then((r) => r.data),
  photoUrl: (id, attId) => `${API}/interactions/${id}/photo/${attId}`,
  confirm: (id, structured, kol_id, transcript) => client.post(`/interactions/${id}/confirm`, { structured, kol_id: kol_id || null, transcript: transcript || null }).then((r) => r.data),
  transcribe: (formData) => client.post("/voice/transcribe", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  interactions: (kolId) => client.get("/interactions", { params: kolId ? { kol_id: kolId } : {} }).then((r) => r.data),
  search: (payload) => client.post("/memory/search", payload).then((r) => r.data),
  followups: (status) => client.get("/followups", { params: status ? { status } : {} }).then((r) => r.data),
  updateFollowup: (id, fields) => { const fd = new FormData(); const f = typeof fields === "string" ? { status: fields } : fields; Object.entries(f).forEach(([k, v]) => fd.append(k, v)); return client.patch(`/followups/${id}`, fd).then((r) => r.data); },
  draftEmail: (id) => client.post(`/followups/${id}/draft-email`).then((r) => r.data),
  flags: (status) => client.get("/compliance/flags", { params: status ? { status } : {} }).then((r) => r.data),
  updateFlag: (id, status) => { const fd = new FormData(); fd.append("status", status); return client.patch(`/compliance/flags/${id}`, fd).then((r) => r.data); },
  audit: () => client.get("/audit").then((r) => r.data),
  literature: (id, refresh = false) => client.get(`/kols/${id}/literature`, { params: refresh ? { refresh: true } : {} }).then((r) => r.data),
  calendarSettings: () => client.get("/calendar/settings").then((r) => r.data),
  calendarConnect: (ics_url) => client.post("/calendar/connect", { ics_url }).then((r) => r.data),
  calendarSync: () => client.post("/calendar/sync").then((r) => r.data),
  calendarDisconnect: () => client.delete("/calendar/disconnect").then((r) => r.data),
  calendarEngagements: () => client.get("/calendar/engagements").then((r) => r.data),
  meetingBrief: (id) => client.get(`/meeting-brief/${id}`).then((r) => r.data),
  genMeetingBrief: (id) => client.post(`/meeting-brief/${id}`).then((r) => r.data),
  kpi: () => client.get("/kpi").then((r) => r.data),
  genKpi: () => client.post("/kpi/generate").then((r) => r.data),
  command: () => client.get("/command").then((r) => r.data),
  commandGenerate: () => client.post("/command/generate").then((r) => r.data),
  execSnapshot: (kolId) => client.get(`/kols/${kolId}/exec-snapshot`).then((r) => r.data),
  genExecSnapshot: (kolId) => client.post(`/kols/${kolId}/exec-snapshot`).then((r) => r.data),
  team: () => client.get("/team").then((r) => r.data),
  teamProfile: (slug) => client.get(`/team/${slug}`).then((r) => r.data),
  genTeamProfile: (slug) => client.post(`/team/${slug}/generate`).then((r) => r.data),
  teamExpertise: (q) => client.get("/team-expertise", { params: { q } }).then((r) => r.data),
  nudges: () => client.get("/assistant/nudges").then((r) => r.data),
  genNudges: () => client.post("/assistant/nudges/generate").then((r) => r.data),
  dismissNudge: (id) => client.post(`/assistant/nudges/${id}/dismiss`).then((r) => r.data),
  assistantChat: (message, session_id) => client.post("/assistant/chat", { message, session_id }).then((r) => r.data),
  chatHistory: (session_id) => client.get(`/assistant/chat/${session_id}`).then((r) => r.data),
  graph: () => client.get("/graph").then((r) => r.data),
  genGraph: () => client.post("/graph/generate").then((r) => r.data),
  graphViews: () => client.get("/graph/views").then((r) => r.data),
  saveGraphView: (name, state) => client.post("/graph/views", { name, state }).then((r) => r.data),
  deleteGraphView: (id) => client.delete(`/graph/views/${id}`).then((r) => r.data),
  governance: () => client.get("/governance").then((r) => r.data),
  addOverride: (payload) => client.post("/governance/overrides", payload).then((r) => r.data),
  deleteOverride: (id) => client.delete(`/governance/overrides/${id}`).then((r) => r.data),
  excludeInteraction: (id, excluded, reason = "") => client.post(`/governance/interactions/${id}/exclude`, { excluded, reason }).then((r) => r.data),
  mergeKols: (primary_id, duplicate_id) => client.post("/governance/kols/merge", { primary_id, duplicate_id }).then((r) => r.data),
  compositeConfig: () => client.get("/impact/composite").then((r) => r.data),
  setCompositeConfig: (enabled, weights) => client.put("/impact/composite", { enabled, weights }).then((r) => r.data),
  adminWorkspace: () => client.get("/admin/workspace").then((r) => r.data),
  createTenant: (name, environment) => client.post("/admin/tenants", { name, environment }).then((r) => r.data),
  createTenantUser: (tenantId, payload) => client.post(`/admin/tenants/${tenantId}/users`, payload).then((r) => r.data),
  migrationPreview: () => client.get("/admin/migration/preview").then((r) => r.data),
  migrationApply: () => client.post("/admin/migration/apply").then((r) => r.data),
  migrationRollback: (id) => client.post(`/admin/migration/${id}/rollback`).then((r) => r.data),
  reportTemplates: () => client.get("/reports/templates").then((r) => r.data),
  reportOptions: () => client.get("/reports/options").then((r) => r.data),
  reportGenerate: (payload) => client.post("/reports/generate", payload).then((r) => r.data),
  reportExports: () => client.get("/reports/exports").then((r) => r.data),
  reportRerun: (id) => client.post(`/reports/exports/${id}/rerun`).then((r) => r.data),
  reportDownloadUrl: (id) => `${API}/reports/exports/${id}/download`,
  reportConfigs: () => client.get("/reports/configs").then((r) => r.data),
  saveReportConfig: (payload) => client.post("/reports/configs", payload).then((r) => r.data),
  deleteReportConfig: (id) => client.delete(`/reports/configs/${id}`).then((r) => r.data),
  adminAnalytics: (days) => client.get(`/admin/analytics?days=${days}`).then((r) => r.data),
};
